import React from 'react';
import { Link, Outlet, useLocation } from 'react-router-dom';
import { 
  Users, 
  UserPlus, 
  GraduationCap, 
  ClipboardList,
  ChevronRight,
  Search,
  Plus
} from 'lucide-react';
import { Button } from '../../components/ui/button';

/**
 * People Workspace
 * 
 * Contains: Employees, Caregivers, HR, Hiring, Training
 * 
 * Note: People = Your team (employees, caregivers, staff)
 * Clients belong in Operations (people you serve)
 */

const PEOPLE_SECTIONS = [
  {
    id: 'employees',
    name: 'Employees',
    description: 'View and manage all employees',
    icon: Users,
    path: '/people/employees',
    color: 'bg-blue-500',
    stats: { label: 'Active', value: '—' }
  },
  {
    id: 'caregivers',
    name: 'Caregivers',
    description: 'Caregivers and field staff',
    icon: Users,
    path: '/people/caregivers',
    color: 'bg-emerald-500',
    stats: { label: 'Active', value: '—' }
  },
  {
    id: 'hiring',
    name: 'Hiring',
    description: 'Applications and onboarding',
    icon: UserPlus,
    path: '/people/hiring',
    color: 'bg-purple-500',
    stats: { label: 'Pending', value: '—' }
  },
  {
    id: 'training',
    name: 'Training',
    description: 'Certifications and training records',
    icon: GraduationCap,
    path: '/people/training',
    color: 'bg-amber-500',
    stats: { label: 'Expiring Soon', value: '—' }
  },
  {
    id: 'hr',
    name: 'HR Dashboard',
    description: 'HR overview and compliance',
    icon: ClipboardList,
    path: '/people/hr',
    color: 'bg-pink-500',
    stats: null
  },
];

export default function PeopleWorkspace() {
  const location = useLocation();
  const isLanding = location.pathname === '/people';

  // If we're on a sub-route, render the Outlet
  if (!isLanding) {
    return <Outlet />;
  }

  return (
    <div className="min-h-screen bg-[#FBFBFA]">
      <div className="max-w-6xl mx-auto px-6 py-8">
        
        {/* Header */}
        <header className="mb-8">
          <div className="flex items-center justify-between mb-2">
            <h1 className="text-2xl font-semibold text-[#0D1B33]">People</h1>
            <div className="flex items-center gap-3">
              <div className="relative">
                <Search className="w-4 h-4 absolute left-3 top-1/2 -translate-y-1/2 text-[#64748B]" />
                <input 
                  type="text"
                  placeholder="Search people..."
                  className="pl-9 pr-4 py-2 text-sm border border-[#E2E8F0] rounded-lg focus:outline-none focus:ring-2 focus:ring-[#176DFF]/20 focus:border-[#176DFF] w-64"
                />
              </div>
              <Link to="/people/employees/new">
                <Button className="bg-[#176DFF] hover:bg-[#176DFF]/90">
                  <Plus className="w-4 h-4 mr-2" />
                  Add Person
                </Button>
              </Link>
            </div>
          </div>
          <p className="text-[#64748B]">
            Manage your team — employees, caregivers, hiring, and training.
          </p>
        </header>

        {/* Section Cards */}
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
          {PEOPLE_SECTIONS.map((section) => (
            <Link
              key={section.id}
              to={section.path}
              className="bg-white rounded-xl border border-[#E2E8F0] p-5 hover:border-[#176DFF]/30 hover:shadow-sm transition-all group"
            >
              <div className="flex items-start justify-between mb-4">
                <div className={`w-10 h-10 rounded-lg flex items-center justify-center text-white ${section.color}`}>
                  <section.icon className="w-5 h-5" />
                </div>
                <ChevronRight className="w-5 h-5 text-[#CBD5E1] group-hover:text-[#176DFF] group-hover:translate-x-1 transition-all" />
              </div>
              <h3 className="font-semibold text-[#0D1B33] mb-1">{section.name}</h3>
              <p className="text-sm text-[#64748B] mb-3">{section.description}</p>
              {section.stats && (
                <div className="flex items-center gap-2 text-xs">
                  <span className="text-[#64748B]">{section.stats.label}:</span>
                  <span className="font-semibold text-[#0D1B33]">{section.stats.value}</span>
                </div>
              )}
            </Link>
          ))}
        </div>
      </div>
    </div>
  );
}
